import * as yup from 'yup'
import type { Dayjs } from 'dayjs'
import { OnboardingStep } from 'src/constants/payments-ops/bankOnboarding'
import {
  EntityIdentificationType,
  LegalStructure,
  PersonIdentificationType,
} from 'src/constants/payments-ops/enums'

interface StepValidatorContext {
  blockedPartnerReasons: Record<string, string>
}

const COUNTRY_PATTERN = /^[A-Za-z]{2}$/
const STATE_PATTERN = /^[A-Za-z]{2}$/

const digitsOf = (value: unknown): string => {
  return String(value ?? '').replace(/\D/g, '')
}

const requiredText = (message: string) => {
  return yup.string().test('required', message, (value) => {
    return (value ?? '').trim().length > 0
  })
}

/** Membership via `.test()` — yup 1.x `oneOf` skips the `undefined` Formik makes of ''. */
const requiredEnum = (allowed: string[], message: string) => {
  return yup.string().test('one-of', message, (value) => {
    return allowed.includes(value ?? '')
  })
}

const digitsLength = (length: number, message: string) => {
  return yup.string().test('digits', message, (value) => {
    return digitsOf(value).length === length
  })
}

const naicsCode = (message: string) => {
  return yup.string().test('naics', message, (value) => {
    const text = (value ?? '').trim()

    return /^\d{2,6}$/.test(text)
  })
}

/** Whole dollars only: commas, spaces and a leading '$' are allowed; decimals, suffixes and 0 are not. */
const wholeDollars = (message: string) => {
  return yup.string().test('whole-dollars', message, (value) => {
    const normalized = (value ?? '').replace(/[,\s]/g, '').replace(/^\$/, '')

    return /^\d+$/.test(normalized) && Number(normalized) > 0
  })
}

const country = (message: string) => {
  return yup.string().test('iso2', message, (value) => {
    return COUNTRY_PATTERN.test((value ?? '').trim())
  })
}

const stateCode = (message: string) => {
  return yup.string().test('state-code', message, (value) => {
    return STATE_PATTERN.test((value ?? '').trim())
  })
}

const requiredPastDate = (message: string) => {
  return yup.mixed<Dayjs>().test('date', message, (value) => {
    return Boolean(value && value.isValid() && !value.isAfter(new Date()))
  })
}

const PERSON_ID_TYPES = Object.values(PersonIdentificationType) as string[]

export const beneficialOwnerValidator = yup.object({
  firstName: requiredText('First name is required'),
  lastName: requiredText('Last name is required'),
  ownershipPercentage: yup.string().test('percentage', 'Enter 0–100', (value) => {
    const percentage = Number(value)

    return (
      (value ?? '').trim() !== '' &&
      Number.isFinite(percentage) &&
      percentage >= 0 &&
      percentage <= 100
    )
  }),
  identificationType: requiredEnum(PERSON_ID_TYPES, 'Choose SSN or ITIN'),
  identificationNumber: digitsLength(9, 'Enter the 9-digit SSN/ITIN'),
  identificationIssuingCountry: country('Two-letter country code'),
  dateOfBirth: requiredPastDate('Date of birth is required'),
  phoneNumber: digitsLength(10, 'Enter a 10-digit phone number'),
  addressLine1: requiredText('Address is required'),
  city: requiredText('City is required'),
  state: stateCode('Two-letter state code'),
  zipCode: requiredText('ZIP code is required'),
  country: country('Two-letter country code'),
})

export const buildOnboardingStepValidator = (
  step: OnboardingStep,
  context: StepValidatorContext,
): yup.AnyObjectSchema => {
  switch (step) {
    case OnboardingStep.PartnerAndProgramManager:
      return yup.object({
        partnerId: yup
          .string()
          .test('required', 'Choose a partner', (value) => {
            return (value ?? '').trim().length > 0
          })
          .test('not-blocked', '', function validatePartner(value) {
            const reason = context.blockedPartnerReasons[value ?? '']

            if (!reason) {
              return true
            }

            return this.createError({ message: `This partner has an ${reason}` })
          }),
        virtualAccountNickName: requiredText('Virtual account nickname is required'),
        programManagerIdentificationNumber: digitsLength(9, 'Enter the 9-digit GigSafe EIN'),
        programManagerName: requiredText('Program manager name is required'),
        contactName: requiredText('Contact name is required'),
        contactEmailAddress: yup
          .string()
          .test('required', 'Contact email is required', (value) => {
            return (value ?? '').trim().length > 0
          })
          .email('Enter a valid email'),
      })

    case OnboardingStep.Business:
      return yup.object({
        legalName: requiredText('Legal name is required'),
        entityIdentificationType: requiredEnum(
          Object.values(EntityIdentificationType),
          'Choose EIN or TIN',
        ),
        entityIdentificationNumber: digitsLength(9, 'Enter the 9-digit EIN/TIN'),
        legalStructure: requiredEnum(Object.values(LegalStructure), 'Choose a legal structure'),
        dbaName: yup.string().when('hasAnyDba', {
          is: true,
          then: (schema) => {
            return schema.test('required', 'DBA name is required', (value) => {
              return (value ?? '').trim().length > 0
            })
          },
        }),
        naicsCode: naicsCode('Enter a 2–6 digit NAICS code'),
        natureOfBusiness: requiredText('Nature of business is required'),
        projectedAnnualRevenue: wholeDollars('Enter whole dollars, digits only'),
        businessPhoneNumber: digitsLength(10, 'Enter a 10-digit phone number'),
        countryOfFormation: country('Two-letter country code'),
        countryOfPrimaryBusinessOperations: country('Two-letter country code'),
        programParticipantAttestation: yup.boolean().test('locked-on', 'Required', (value) => {
          return value === true
        }),
      })

    case OnboardingStep.BusinessAddress:
      return yup.object({
        businessAddressLine1: requiredText('Address is required'),
        businessCity: requiredText('City is required'),
        businessState: stateCode('Two-letter state code'),
        businessZipCode: requiredText('ZIP code is required'),
        businessCountry: country('Two-letter country code'),
      })

    case OnboardingStep.BeneficialOwners:
      return yup.object({
        beneficialOwners: yup.array().when('hasAnyBeneficialOwner', {
          is: true,
          then: (schema) => {
            return schema
              .min(1, 'Add at least one beneficial owner')
              .test('owners-complete', '', function validateOwners(owners) {
                const rows = owners ?? []
                const invalidIndex = rows.findIndex((row) => {
                  return !beneficialOwnerValidator.isValidSync(row)
                })

                if (invalidIndex === -1) {
                  return true
                }

                return this.createError({
                  message: `Beneficial owner ${invalidIndex + 1} is incomplete — edit it to finish`,
                })
              })
          },
        }),
      })

    case OnboardingStep.ControlPerson:
      return yup.object({
        controlPersonTitle: requiredText('Title is required'),
        controlPersonFirstName: requiredText('First name is required'),
        controlPersonLastName: requiredText('Last name is required'),
        controlPersonIdentificationType: requiredEnum(PERSON_ID_TYPES, 'Choose SSN or ITIN'),
        controlPersonIdentificationNumber: digitsLength(9, 'Enter the 9-digit SSN/ITIN'),
        controlPersonIssuingCountry: country('Two-letter country code'),
        controlPersonDateOfBirth: requiredPastDate('Date of birth is required'),
        controlPersonPhoneNumber: digitsLength(10, 'Enter a 10-digit phone number'),
        controlPersonAddressLine1: requiredText('Address is required'),
        controlPersonCity: requiredText('City is required'),
        controlPersonState: stateCode('Two-letter state code'),
        controlPersonZipCode: requiredText('ZIP code is required'),
        controlPersonCountry: country('Two-letter country code'),
      })

    case OnboardingStep.Attestor:
      return yup.object({
        attestorFirstName: requiredText('First name is required'),
        attestorLastName: requiredText('Last name is required'),
        attestorJobTitle: requiredText('Job title is required'),
        dateOfAttestation: requiredPastDate('Attestation date is required'),
      })

    default:
      return yup.object({})
  }
}
